const padDatePart = (value) => String(value).padStart(2, "0");

const toFourDigitYear = (value) => {
  const year = Number(value);
  if (!Number.isInteger(year)) return "";
  if (String(value).length === 2) return String(year >= 70 ? 1900 + year : 2000 + year);
  return String(year);
};

const isValidIsoDate = (candidate) => {
  const [year, month, day] = candidate.split("-").map(Number);
  const parsed = new Date(`${candidate}T00:00:00`);
  return (
    !Number.isNaN(parsed.getTime()) &&
    parsed.getFullYear() === year &&
    parsed.getMonth() + 1 === month &&
    parsed.getDate() === day
  );
};

export const normalizeEventDate = (value) => {
  const text = String(value || "").trim();
  if (!text) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return isValidIsoDate(text) ? text : "";

  const match = text.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{2}|\d{4})$/);
  if (!match) return "";

  const [, day, month, rawYear] = match;
  const year = toFourDigitYear(rawYear);
  const candidate = `${year}-${padDatePart(month)}-${padDatePart(day)}`;
  return isValidIsoDate(candidate) ? candidate : "";
};

export const normalizeEventTime = (value) => {
  const text = String(value || "").trim();
  const match = text.match(/^(\d{1,2}):([0-5]\d)$/);
  if (!match) return "";
  const hour = Number(match[1]);
  if (hour < 0 || hour > 23) return "";
  return `${String(hour).padStart(2, "0")}:${match[2]}`;
};

export const formatEventDateForDisplay = (value) => {
  const normalized = normalizeEventDate(value);
  if (!normalized) return "";
  const [year, month, day] = normalized.split("-");
  return `${day}/${month}/${year}`;
};

export const getLocalIsoDate = (value = new Date()) => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return [
    date.getFullYear(),
    padDatePart(date.getMonth() + 1),
    padDatePart(date.getDate()),
  ].join("-");
};

export const getEventDateTime = (details = {}) => {
  const date = normalizeEventDate(details.date);
  const time = normalizeEventTime(details.time);
  if (!date || !time) return null;
  const eventDateTime = new Date(`${date}T${time}:00`);
  return Number.isNaN(eventDateTime.getTime()) ? null : eventDateTime;
};

export const isEventDateTimeInFuture = (details = {}, now = new Date()) => {
  const eventDateTime = getEventDateTime(details);
  const currentDateTime = now instanceof Date ? now : new Date(now);
  return Boolean(
    eventDateTime &&
    !Number.isNaN(currentDateTime.getTime()) &&
    eventDateTime.getTime() > currentDateTime.getTime()
  );
};

export const getEventCountdown = (details = {}, now = new Date()) => {
  const eventDateTime = getEventDateTime(details);
  const currentDateTime = now instanceof Date ? now : new Date(now);
  if (!eventDateTime || Number.isNaN(currentDateTime.getTime())) return null;

  const difference = eventDateTime.getTime() - currentDateTime.getTime();
  if (difference <= 0) return { expired: true, days: 0, hours: 0, minutes: 0, seconds: 0 };

  const totalSeconds = Math.ceil(difference / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return { expired: false, days, hours, minutes, seconds };
};

export const getStoredEventDetails = (data = {}) => {
  const stored = data.eventDetails && typeof data.eventDetails === "object"
    ? data.eventDetails
    : {};
  const invitationData = data.invitationData && typeof data.invitationData === "object"
    ? data.invitationData
    : {};

  return {
    title: String(data.title || invitationData.eventOwners || "").trim(),
    date: normalizeEventDate(stored.date || data.eventDate || invitationData.eventDate || invitationData.date),
    time: normalizeEventTime(stored.time || data.eventTime || invitationData.eventTime),
    venueName: String(stored.venueName || data.eventVenueName || invitationData.locationName || "").trim(),
    address: String(stored.address || data.eventAddress || invitationData.locationAddress || "").trim(),
  };
};

export const normalizeEventDetails = (details = {}) => ({
  title: String(details.title || "").trim(),
  date: normalizeEventDate(details.date),
  time: normalizeEventTime(details.time),
  venueName: String(details.venueName || "").trim(),
  address: String(details.address || "").trim(),
});

export const getMissingEventDetailKeys = (details = {}) => {
  const normalized = normalizeEventDetails(details);
  return ["title", "date", "time", "venueName", "address"].filter((key) => !normalized[key]);
};

export const hasSentEventInvitations = (messages = []) => (
  Array.isArray(messages) && messages.some((message) => {
    if (!message || message.status === "cancelled" || message.status === "error") return false;
    if (message.sentImageCapturedAt || message.sentInvitationImageUrl) return true;
    const scheduledTime = new Date(String(message.timeToSend || "").replace(" ", "T")).getTime();
    return Number.isFinite(scheduledTime) && scheduledTime < Date.now();
  })
);

export const eventDetailsChanged = (current, saved) => {
  const nextDetails = normalizeEventDetails(current);
  const savedDetails = normalizeEventDetails(saved);
  return ["date", "time", "venueName", "address"].some((key) => nextDetails[key] !== savedDetails[key]);
};

export const isEventScheduleLocked = (data = {}) => (
  String(data.autopilotLifecycle?.status || "inactive") !== "inactive"
);
